"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { format } from "date-fns/format";
import { isValid } from "date-fns/isValid";
import { parse } from "date-fns/parse";
import { startOfDay } from "date-fns/startOfDay";
import { CalendarDays, Clock, X } from "lucide-react";
import { cn } from "lib/utils";
import { Calendar } from "components/ui/calendar";

/* ══════════════════════════════════════════════════════════════════════════
   Date and time controls.

   These replace `<input type="date">`, `<input type="time">` and
   `<input type="datetime-local">`, all of which are drawn by the operating
   system. A className styles the closed field and nothing else: the moment one
   opens, the picker is the OS's, so it ignored the theme, ignored dark mode,
   and looked like a different product on macOS, Windows and Android.

   The value contract is unchanged on purpose. Callers still hand over and
   receive `yyyy-MM-dd` (or `HH:mm`), exactly what the native input emitted, so
   a migration is a swap of the element and nothing else — no payload changes,
   no date-parsing changes at the call site.
   ══════════════════════════════════════════════════════════════════════════ */

const FIELD =
    "inline-flex items-center gap-2 h-10 w-full min-w-0 px-3 rounded-xl " +
    "bg-surface border border-line text-ui font-medium text-content " +
    "transition-[border-color,box-shadow] duration-150 ease-ds " +
    "hover:border-line-strong focus-ring " +
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-line";

const PANEL =
    "z-[120] rounded-xl border border-line bg-surface shadow-elevated p-3 " +
    "animate-overlay-in";

export const DATE_VALUE_FORMAT = "yyyy-MM-dd";

const toDate = (v?: string | null): Date | null => {
    if (!v) return null;
    const d = parse(v, DATE_VALUE_FORMAT, new Date());
    return isValid(d) ? d : null;
};

export interface DatePickerProps {
    /** `yyyy-MM-dd`, the same string `<input type="date">` produced */
    value?: string | null;
    onChange?: (value: string) => void;
    min?: string | null;
    max?: string | null;
    placeholder?: string;
    disabled?: boolean;
    /** Shows an × once a date is set */
    clearable?: boolean;
    id?: string;
    className?: string;
    "aria-label"?: string;
    "aria-describedby"?: string;
    align?: "start" | "center" | "end";
}

export const DatePicker: React.FC<DatePickerProps> = ({
    value,
    onChange,
    min,
    max,
    placeholder = "Select a date",
    disabled,
    clearable = true,
    id,
    className,
    align = "start",
    ...aria
}) => {
    const [open, setOpen] = React.useState(false);
    const selected = toDate(value);

    const commit = (d: Date) => {
        onChange?.(format(startOfDay(d), DATE_VALUE_FORMAT));
        setOpen(false);
    };

    return (
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
            <PopoverPrimitive.Trigger asChild>
                <button
                    type="button"
                    id={id}
                    disabled={disabled}
                    className={cn(FIELD, "justify-between text-left", className)}
                    {...aria}
                >
                    <span className="inline-flex items-center gap-2 min-w-0">
                        <CalendarDays className="w-4 h-4 flex-shrink-0 text-content-subtle" aria-hidden="true" />
                        <span className={cn("truncate", !selected && "text-content-subtle font-normal")}>
                            {selected ? format(selected, "d MMM yyyy") : placeholder}
                        </span>
                    </span>

                    {clearable && selected && !disabled && (
                        <span
                            role="button"
                            tabIndex={0}
                            aria-label="Clear date"
                            onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onChange?.("");
                                }
                            }}
                            className="flex-shrink-0 p-0.5 rounded-md text-content-subtle hover:text-content hover:bg-surface-hover transition-colors"
                        >
                            <X className="w-3.5 h-3.5" />
                        </span>
                    )}
                </button>
            </PopoverPrimitive.Trigger>

            <PopoverPrimitive.Portal>
                <PopoverPrimitive.Content align={align} sideOffset={6} className={PANEL}>
                    <Calendar
                        value={selected}
                        onChange={commit}
                        minDate={toDate(min)}
                        maxDate={toDate(max)}
                        aria-label={aria["aria-label"] ?? "Choose a date"}
                    />
                    <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-line-subtle">
                        <button
                            type="button"
                            onClick={() => commit(new Date())}
                            className="text-meta font-semibold text-brand hover:text-brand-hover px-2 py-1 rounded-lg hover:bg-brand-subtle transition-colors focus-ring"
                        >
                            Today
                        </button>
                        {clearable && (
                            <button
                                type="button"
                                onClick={() => { onChange?.(""); setOpen(false); }}
                                className="text-meta font-semibold text-content-muted hover:text-content px-2 py-1 rounded-lg hover:bg-surface-hover transition-colors focus-ring"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
    );
};

/* ── Time ────────────────────────────────────────────────────────────────
   A list, not a spinner. `<input type="time">` on a desktop browser is three
   tiny stepper fields; picking 09:30 means typing into two of them or
   clicking an arrow thirty times. Fifteen-minute steps cover almost every
   real choice in one scroll, and the field still accepts any exact value the
   caller sets programmatically.                                            */

export interface TimePickerProps {
    /** `HH:mm`, the same string `<input type="time">` produced */
    value?: string | null;
    onChange?: (value: string) => void;
    /** Minutes between offered options */
    step?: 5 | 10 | 15 | 30 | 60;
    /** 12-hour labels; the emitted value stays 24-hour either way */
    hour12?: boolean;
    placeholder?: string;
    disabled?: boolean;
    id?: string;
    className?: string;
    "aria-label"?: string;
}

export const TimePicker: React.FC<TimePickerProps> = ({
    value,
    onChange,
    step = 15,
    hour12 = true,
    placeholder = "Select a time",
    disabled,
    id,
    className,
    ...aria
}) => {
    const [open, setOpen] = React.useState(false);
    const listRef = React.useRef<HTMLDivElement>(null);

    const options = React.useMemo(() => {
        const out: Array<{ value: string; label: string }> = [];
        for (let m = 0; m < 24 * 60; m += step) {
            const h = Math.floor(m / 60);
            const mm = m % 60;
            const v = `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
            const d = new Date(2000, 0, 1, h, mm);
            out.push({ value: v, label: format(d, hour12 ? "h:mm a" : "HH:mm") });
        }
        return out;
    }, [step, hour12]);

    const label = React.useMemo(() => {
        if (!value) return null;
        const [h, m] = value.split(":").map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return value;
        return format(new Date(2000, 0, 1, h, m), hour12 ? "h:mm a" : "HH:mm");
    }, [value, hour12]);

    // Opens on the current value rather than at midnight.
    React.useEffect(() => {
        if (!open || !value) return;
        const t = window.setTimeout(() => {
            listRef.current
                ?.querySelector<HTMLElement>(`[data-time="${value}"]`)
                ?.scrollIntoView({ block: "center" });
        }, 0);
        return () => window.clearTimeout(t);
    }, [open, value]);

    return (
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
            <PopoverPrimitive.Trigger asChild>
                <button
                    type="button"
                    id={id}
                    disabled={disabled}
                    className={cn(FIELD, "justify-start text-left", className)}
                    {...aria}
                >
                    <Clock className="w-4 h-4 flex-shrink-0 text-content-subtle" aria-hidden="true" />
                    <span className={cn("truncate", !label && "text-content-subtle font-normal")}>
                        {label ?? placeholder}
                    </span>
                </button>
            </PopoverPrimitive.Trigger>

            <PopoverPrimitive.Portal>
                <PopoverPrimitive.Content align="start" sideOffset={6} className={cn(PANEL, "p-1")}>
                    <div
                        ref={listRef}
                        role="listbox"
                        aria-label={aria["aria-label"] ?? "Choose a time"}
                        className="max-h-[15rem] w-[9rem] overflow-y-auto overscroll-contain"
                    >
                        {options.map((o) => {
                            const active = o.value === value;
                            return (
                                <button
                                    key={o.value}
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    data-time={o.value}
                                    onClick={() => { onChange?.(o.value); setOpen(false); }}
                                    className={cn(
                                        "w-full text-left px-3 py-1.5 rounded-lg text-ui font-medium tabular-nums transition-colors focus-ring",
                                        active
                                            ? "bg-brand text-brand-fg font-semibold"
                                            : "text-content hover:bg-surface-hover"
                                    )}
                                >
                                    {o.label}
                                </button>
                            );
                        })}
                    </div>
                </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
    );
};

export default DatePicker;
